import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CreditCard,
  Plus,
  RefreshCw,
  Trash2,
  Wallet as WalletIcon,
} from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { useToast } from '../../../components/ui/Toast';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import useReceivables from '../hooks/useReceivables';
import './ReceivablesTab.css';

const STATUS_FILTERS = [
  { id: 'open',           label: 'Outstanding' },
  { id: 'PAID',           label: 'Paid' },
  { id: 'WRITTEN_OFF',    label: 'Written off' },
];

const PAYMENT_METHODS = [
  { id: 'CASH',          label: 'Cash' },
  { id: 'BANK_TRANSFER', label: 'Bank transfer' },
  { id: 'CARD',          label: 'Card' },
  { id: 'MOBILE_MONEY',  label: 'Mobile money' },
  { id: 'CHEQUE',        label: 'Cheque' },
  { id: 'OTHER',         label: 'Other' },
];

const BUCKET_META = [
  { id: 'current',      label: 'Current',  tone: 'good' },
  { id: 'bucket0_30',   label: '0–30 days', tone: 'info' },
  { id: 'bucket31_60',  label: '31–60 days', tone: 'warn' },
  { id: 'bucket61_90',  label: '61–90 days', tone: 'danger' },
  { id: 'bucket90plus', label: '90+ days',   tone: 'danger' },
];

const STATUS_BADGE = {
  OPEN:            { label: 'Open',           tone: 'open' },
  PARTIALLY_PAID:  { label: 'Partially paid', tone: 'partial' },
  PAID:            { label: 'Paid',           tone: 'paid' },
  WRITTEN_OFF:     { label: 'Written off',    tone: 'writeoff' },
};

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
});
const fmtMoney = (n) => NAIRA.format(Number(n || 0));
const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString('en-NG', {
  year: 'numeric', month: 'short', day: 'numeric',
}) : '—';

function bucketTone(bucket) {
  return BUCKET_META.find((b) => b.id === bucket)?.tone || 'good';
}
function bucketLabel(bucket) {
  return BUCKET_META.find((b) => b.id === bucket)?.label || '—';
}

/* ─── Sub-components (module scope) ─────────────────────────────── */

function AgingStrip({ aging, loading }) {
  if (loading) {
    return (
      <div className="fin-rcv-aging">
        {BUCKET_META.map((b) => (
          <div key={b.id} className="fin-rcv-aging-card fin-rcv-aging-card--skeleton">
            <div className="fin-skel-line fin-skel-line--sm" />
            <div className="fin-skel-line fin-skel-line--md" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="fin-rcv-aging">
      {BUCKET_META.map((b) => {
        const bucket = aging.buckets?.[b.id] || { count: 0, amount: 0 };
        return (
          <div key={b.id} className={`fin-rcv-aging-card fin-rcv-aging-card--${b.tone}`}>
            <p className="fin-rcv-aging-label">{b.label}</p>
            <p className="fin-rcv-aging-amount">{fmtMoney(bucket.amount)}</p>
            <p className="fin-rcv-aging-count">
              {bucket.count} {bucket.count === 1 ? 'receivable' : 'receivables'}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function StatusFilterPills({ value, onChange }) {
  return (
    <div className="fin-rcv-filter" role="tablist" aria-label="Receivable status">
      {STATUS_FILTERS.map((s) => (
        <button
          key={s.id}
          type="button"
          role="tab"
          aria-selected={value === s.id}
          className={`fin-rcv-filter-pill${value === s.id ? ' is-active' : ''}`}
          onClick={() => onChange(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_BADGE[status] || { label: status, tone: 'open' };
  return <span className={`fin-rcv-status fin-rcv-status--${meta.tone}`}>{meta.label}</span>;
}

function RowActions({ item, onPay, onRemind, onWriteOff }) {
  const closed = item.status === 'PAID' || item.status === 'WRITTEN_OFF';
  return (
    <div className="fin-rcv-actions">
      <button
        type="button"
        className="fin-rcv-action fin-rcv-action--primary"
        onClick={() => onPay(item)}
        disabled={closed}
      >
        <WalletIcon size={13} /> Record payment
      </button>
      <button
        type="button"
        className="fin-rcv-action"
        onClick={() => onRemind(item)}
        disabled={closed}
      >
        <Bell size={13} /> Remind
      </button>
      <button
        type="button"
        className="fin-rcv-action fin-rcv-action--danger"
        onClick={() => onWriteOff(item)}
        disabled={closed}
      >
        <Trash2 size={13} /> Write off
      </button>
    </div>
  );
}

/* Mobile card. Urgency lives in the aging chip (own row) — no
   coloured rail on the card itself; that read as visual noise on
   small screens and clashed with the rest of the Finance chrome. */
function ReceivableCard({ item, onPay, onRemind, onWriteOff }) {
  const tone = bucketTone(item.bucket);
  return (
    <div className="fin-rcv-card">
      <div className="fin-rcv-card-head">
        <div className="fin-rcv-card-customer">
          <p className="fin-rcv-card-name" title={item.customerName}>{item.customerName}</p>
          <p className="fin-rcv-card-sub">
            {item.sourceOrder?.orderNumber
              ? <>Order {item.sourceOrder.orderNumber}</>
              : <>Manual IOU</>}
            <span className="fin-rcv-card-bullet" aria-hidden>·</span>
            Due {fmtDate(item.dueDate)}
          </p>
        </div>
        <StatusBadge status={item.status} />
      </div>

      <span className={`fin-rcv-card-aging fin-rcv-card-aging--${tone}`}>
        <span className="fin-rcv-card-aging-dot" aria-hidden />
        {bucketLabel(item.bucket)}
      </span>

      <div className="fin-rcv-card-amounts">
        <div>
          <p className="fin-rcv-card-stat-label">Owed</p>
          <p className="fin-rcv-card-stat-value">{fmtMoney(item.remaining)}</p>
        </div>
        <div>
          <p className="fin-rcv-card-stat-label">Paid</p>
          <p className="fin-rcv-card-stat-value">{fmtMoney(item.paidAmount)}</p>
        </div>
        <div>
          <p className="fin-rcv-card-stat-label">Original</p>
          <p className="fin-rcv-card-stat-value">{fmtMoney(item.originalAmount)}</p>
        </div>
      </div>

      <RowActions item={item} onPay={onPay} onRemind={onRemind} onWriteOff={onWriteOff} />
    </div>
  );
}

/* Desktop table row */
function ReceivableRow({ item, onPay, onRemind, onWriteOff }) {
  return (
    <tr className="fin-rcv-row">
      <td>
        <div className="fin-rcv-row-customer">
          <span className="fin-rcv-row-name" title={item.customerName}>{item.customerName}</span>
          <span className="fin-rcv-row-source">
            {item.sourceOrder?.orderNumber ? <>Order {item.sourceOrder.orderNumber}</> : 'Manual IOU'}
          </span>
        </div>
      </td>
      <td className="fin-rcv-num">{fmtMoney(item.originalAmount)}</td>
      <td className="fin-rcv-num">{fmtMoney(item.paidAmount)}</td>
      <td className="fin-rcv-num fin-rcv-num--strong">{fmtMoney(item.remaining)}</td>
      <td>{fmtDate(item.dueDate)}</td>
      <td><span className={`fin-rcv-bucket fin-rcv-bucket--${bucketTone(item.bucket)}`}>{bucketLabel(item.bucket)}</span></td>
      <td><StatusBadge status={item.status} /></td>
      <td>
        <RowActions item={item} onPay={onPay} onRemind={onRemind} onWriteOff={onWriteOff} />
      </td>
    </tr>
  );
}

/* ─── Modals ────────────────────────────────────────────────────── */

function LogIouForm({ onSubmit, onCancel, submitting }) {
  const [customerName, setCustomerName] = useState('');
  const [originalAmount, setOriginalAmount] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const num = parseFloat(originalAmount);
    if (!Number.isFinite(num) || num <= 0) return;
    if (!customerName.trim()) return;
    onSubmit({
      customerName: customerName.trim(),
      originalAmount: num,
      dueDate,
      notes: notes.trim() || null,
    });
  };

  return (
    <form className="fin-rcv-form" onSubmit={submit}>
      <div className="fin-rcv-field">
        <label htmlFor="iou-name">Customer name</label>
        <input
          id="iou-name"
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Who owes you?"
          required
          autoFocus
          maxLength={120}
        />
      </div>
      <div className="fin-rcv-form-row">
        <div className="fin-rcv-field">
          <label htmlFor="iou-amount">Amount (₦)</label>
          <input
            id="iou-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={originalAmount}
            onChange={(e) => setOriginalAmount(e.target.value)}
            placeholder="0.00"
            required
          />
        </div>
        <div className="fin-rcv-field">
          <label htmlFor="iou-due">Due date</label>
          <input
            id="iou-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
      </div>
      <div className="fin-rcv-field">
        <label htmlFor="iou-notes">Notes <span className="fin-rcv-optional">(optional)</span></label>
        <textarea
          id="iou-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What's this for?"
          maxLength={500}
        />
      </div>
      <div className="fin-rcv-form-actions">
        <button type="button" className="fin-rcv-btn fin-rcv-btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className="fin-rcv-btn fin-rcv-btn--primary" disabled={submitting}>
          {submitting ? 'Saving…' : 'Log credit sale'}
        </button>
      </div>
    </form>
  );
}

function RecordPaymentForm({ receivable, onSubmit, onCancel, submitting }) {
  const remaining = Number(receivable?.remaining || 0);
  const [amount, setAmount] = useState(String(remaining || ''));
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!Number.isFinite(num) || num <= 0) return;
    if (num > remaining + 0.001) return;
    onSubmit({
      amount: num,
      paymentMethod,
      paidAt,
      notes: notes.trim() || null,
    });
  };

  return (
    <form className="fin-rcv-form" onSubmit={submit}>
      <div className="fin-rcv-payment-context">
        <div>
          <p className="fin-rcv-payment-label">Customer</p>
          <p className="fin-rcv-payment-value">{receivable?.customerName}</p>
        </div>
        <div>
          <p className="fin-rcv-payment-label">Outstanding</p>
          <p className="fin-rcv-payment-value fin-rcv-payment-value--accent">{fmtMoney(remaining)}</p>
        </div>
      </div>

      <div className="fin-rcv-form-row">
        <div className="fin-rcv-field">
          <label htmlFor="pay-amount">Amount received (₦)</label>
          <input
            id="pay-amount"
            type="number"
            step="0.01"
            min="0.01"
            max={remaining}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            autoFocus
          />
          <small className="fin-rcv-help">Cannot exceed outstanding balance.</small>
        </div>
        <div className="fin-rcv-field">
          <label htmlFor="pay-method">Payment method</label>
          <select
            id="pay-method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="fin-rcv-form-row">
        <div className="fin-rcv-field">
          <label htmlFor="pay-date">Date received</label>
          <input
            id="pay-date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
        </div>
        <div className="fin-rcv-field">
          <label htmlFor="pay-notes">Notes <span className="fin-rcv-optional">(optional)</span></label>
          <input
            id="pay-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reference / receipt"
            maxLength={240}
          />
        </div>
      </div>

      <div className="fin-rcv-form-actions">
        <button type="button" className="fin-rcv-btn fin-rcv-btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className="fin-rcv-btn fin-rcv-btn--primary" disabled={submitting}>
          {submitting ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </form>
  );
}

function WriteOffForm({ receivable, onSubmit, onCancel, submitting }) {
  const [reason, setReason] = useState('');
  return (
    <form
      className="fin-rcv-form"
      onSubmit={(e) => { e.preventDefault(); onSubmit({ reason: reason.trim() || null }); }}
    >
      <p className="fin-rcv-writeoff-blurb">
        Writing off marks <strong>{receivable?.customerName}</strong>'s outstanding
        <strong> {fmtMoney(receivable?.remaining)}</strong> as uncollectible. The receivable stays in
        history for reporting; it just stops showing up under outstanding.
      </p>
      <div className="fin-rcv-field">
        <label htmlFor="wo-reason">Reason <span className="fin-rcv-optional">(optional)</span></label>
        <textarea
          id="wo-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer unreachable; bad debt; settlement agreed."
          maxLength={500}
          autoFocus
        />
      </div>
      <div className="fin-rcv-form-actions">
        <button type="button" className="fin-rcv-btn fin-rcv-btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className="fin-rcv-btn fin-rcv-btn--danger" disabled={submitting}>
          {submitting ? 'Writing off…' : 'Confirm write-off'}
        </button>
      </div>
    </form>
  );
}

function EmptyState({ status, onAdd }) {
  if (status === 'open') {
    return (
      <div className="fin-empty">
        <WalletIcon size={40} className="fin-empty-icon" />
        <h2 className="fin-empty-title">No money owed to you right now</h2>
        <p className="fin-empty-sub">
          When you mark a manual sale as CREDIT in the Record Sale flow, the receivable
          lands here automatically. You can also log a manual IOU below.
        </p>
        <button type="button" className="fin-empty-cta" onClick={onAdd}>
          <Plus size={14} style={{ marginRight: 6 }} /> Log credit sale
        </button>
      </div>
    );
  }
  return (
    <div className="fin-empty">
      <WalletIcon size={40} className="fin-empty-icon" />
      <h2 className="fin-empty-title">Nothing in this bucket yet</h2>
      <p className="fin-empty-sub">Switch the filter to see other receivables.</p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

function SkeletonCards({ count = 3 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="fin-rcv-card fin-rcv-card--skeleton">
      <div className="fin-skel-line fin-skel-line--head" />
      <div className="fin-skel-line fin-skel-line--sub" />
      <div className="fin-skel-line fin-skel-line--sub" />
    </div>
  ));
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function ReceivablesTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [status, setStatus] = useState('open');
  const [iouModal, setIouModal] = useState(false);
  const [payModal, setPayModal] = useState({ open: false, item: null });
  const [writeOffModal, setWriteOffModal] = useState({ open: false, item: null });
  const [submitting, setSubmitting] = useState(false);
  const [reminderId, setReminderId] = useState(null);

  const {
    items, aging, loading, error, refetch,
    createIou, recordPayment, writeOff, remind,
  } = useReceivables({ status });

  const filteredItems = useMemo(() => items, [items]);
  const hasItems = filteredItems.length > 0;
  const showEmpty = !loading && !error && !hasItems;

  const closeAll = () => {
    if (submitting) return;
    setIouModal(false);
    setPayModal({ open: false, item: null });
    setWriteOffModal({ open: false, item: null });
  };

  const handleCreateIou = async (payload) => {
    setSubmitting(true);
    try {
      await createIou(payload);
      toast.success('Credit sale logged');
      setIouModal(false);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to log credit sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordPayment = async (payload) => {
    if (!payModal.item) return;
    setSubmitting(true);
    try {
      const res = await recordPayment(payModal.item.id, payload);
      const settled = res?.receivable?.status === 'PAID';
      toast.success(settled ? 'Paid in full!' : 'Payment recorded');
      setPayModal({ open: false, item: null });
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWriteOff = async (payload) => {
    if (!writeOffModal.item) return;
    setSubmitting(true);
    try {
      await writeOff(writeOffModal.item.id, payload.reason);
      toast.success('Receivable written off');
      setWriteOffModal({ open: false, item: null });
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to write off');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemind = async (item) => {
    const ok = await confirm(
      `Email ${item.customerName} a reminder about the ${fmtMoney(item.remaining)} outstanding?`,
      {
        title: 'Send payment reminder',
        confirmText: 'Send reminder',
        cancelText: 'Cancel',
        type: 'warning',
      }
    );
    if (!ok) return;
    setReminderId(item.id);
    try {
      await remind(item.id);
      toast.success('Reminder sent');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Reminder failed';
      toast.error(msg);
    } finally {
      setReminderId(null);
    }
  };

  return (
    <div className="fin-rcv">
      <AgingStrip aging={aging} loading={loading && !hasItems} />

      <div className="fin-rcv-toolbar">
        <StatusFilterPills value={status} onChange={setStatus} />
        <button
          type="button"
          className="fin-rcv-btn fin-rcv-btn--primary fin-rcv-btn--add"
          onClick={() => setIouModal(true)}
        >
          <Plus size={14} /> Log credit sale
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {showEmpty && <EmptyState status={status} onAdd={() => setIouModal(true)} />}

      {loading && !hasItems && (
        <div className="fin-rcv-list">
          <SkeletonCards />
        </div>
      )}

      {hasItems && (
        <>
          <div className="fin-rcv-list">
            {filteredItems.map((item) => (
              <ReceivableCard
                key={item.id}
                item={item}
                onPay={(it) => setPayModal({ open: true, item: it })}
                onRemind={handleRemind}
                onWriteOff={(it) => setWriteOffModal({ open: true, item: it })}
              />
            ))}
          </div>

          <div className="fin-rcv-table-wrap">
            <table className="fin-rcv-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="fin-rcv-num">Original</th>
                  <th className="fin-rcv-num">Paid</th>
                  <th className="fin-rcv-num">Owed</th>
                  <th>Due</th>
                  <th>Aging</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <ReceivableRow
                    key={item.id}
                    item={item}
                    onPay={(it) => setPayModal({ open: true, item: it })}
                    onRemind={handleRemind}
                    onWriteOff={(it) => setWriteOffModal({ open: true, item: it })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {reminderId && (
        <div className="fin-rcv-busy" aria-live="polite">Sending reminder…</div>
      )}

      <Modal
        isOpen={iouModal}
        onClose={closeAll}
        title="Log credit sale"
        size="md"
      >
        <LogIouForm
          onSubmit={handleCreateIou}
          onCancel={closeAll}
          submitting={submitting}
        />
      </Modal>

      <Modal
        isOpen={payModal.open}
        onClose={closeAll}
        title="Record payment"
        size="md"
      >
        <RecordPaymentForm
          receivable={payModal.item}
          onSubmit={handleRecordPayment}
          onCancel={closeAll}
          submitting={submitting}
        />
      </Modal>

      <Modal
        isOpen={writeOffModal.open}
        onClose={closeAll}
        title="Write off receivable"
        size="md"
      >
        <WriteOffForm
          receivable={writeOffModal.item}
          onSubmit={handleWriteOff}
          onCancel={closeAll}
          submitting={submitting}
        />
      </Modal>
    </div>
  );
}
